import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';

// Matches org.authmatech.data.entity.ClientStatus enum
export type ClientStatus =
  | 'ONBOARDING'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'SUSPENDED'
  | 'CANCELLED'
  | 'REVOKED';

// Matches org.authmatech.data.dto.ClientDto exactly
export interface ClientDto {
  id: string;
  clientName: string;
  contactPersonName: string;
  contactPersonEmail: string;
  contactPhoneNumber: string | null;
  dotId: string | null;
  clientStatus: ClientStatus;
  expectedAuthVolume: string | null;
  balance: number;
  balanceExpiryDate: string | null;
  balanceValid: boolean;
  daysUntilBalanceExpiry: number;
  transferExpiredBalance: boolean;
  createdAt: string | null;
}

// Matches org.authmatech.data.dto.PageDto<ClientDto>
export interface ClientsPage {
  content: ClientDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// Matches DashboardSummaryDto
export interface DashboardSummary {
  totalClients: number;
  activeClients: number;
  onboardingClients: number;
  inactiveClients: number;
  cancelledClients: number;
  revokedClients: number;
  todayVerifications: number;
  todaySuccessRate: number;
  todayFailedVerifications: number;
  lowBalanceClientsCount: number;
  expiringBalanceClientsCount: number;
  lowBalanceClients: ClientDto[];
  expiringBalanceClients: ClientDto[];
}

// Matches org.authmatech.data.dto.ClientKeysDto
export interface RegisterResult {
  apiKey: string;
  clientSecret?: string;
  sdkToken?: string;
}

export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  const res = await api.get<ResponseEnvelope<DashboardSummary>>(
    Endpoints.dashboard.summary,
  );
  return unwrap(res);
}

// Query params match DashboardController.listClients() — searchTerm, clientStatus
export async function fetchClients(params?: {
  searchTerm?: string;
  clientStatus?: ClientStatus;
  page?: number;
  size?: number;
}): Promise<ClientsPage> {
  const res = await api.get<ResponseEnvelope<ClientsPage>>(Endpoints.dashboard.clients, {
    params,
  });
  return unwrap(res);
}

export async function fetchClient(id: string): Promise<ClientDto> {
  const res = await api.get<ResponseEnvelope<ClientDto>>(Endpoints.dashboard.client(id));
  return unwrap(res);
}

export async function onboardClient(body: {
  name: string;
  contactPerson: string;
  expectedMonthlyVolume?: number;
}): Promise<ClientDto> {
  const res = await api.post<ResponseEnvelope<ClientDto>>(Endpoints.dashboard.clients, body);
  return unwrap(res);
}

export async function registerClient(id: string): Promise<RegisterResult> {
  const res = await api.post<ResponseEnvelope<RegisterResult>>(
    Endpoints.dashboard.registerClient(id),
  );
  return unwrap(res);
}

export async function blockClient(id: string): Promise<void> {
  const res = await api.post<ResponseEnvelope<void>>(Endpoints.dashboard.blockClient(id));
  unwrap(res);
}

export async function suspendClient(id: string): Promise<void> {
  const res = await api.post<ResponseEnvelope<void>>(Endpoints.dashboard.suspendClient(id));
  unwrap(res);
}

export async function reactivateClient(id: string): Promise<void> {
  const res = await api.post<ResponseEnvelope<void>>(Endpoints.dashboard.reactivateClient(id));
  unwrap(res);
}

export async function fetchMyClient(): Promise<ClientDto> {
  const res = await api.get<ResponseEnvelope<ClientDto>>(Endpoints.dashboard.myClient);
  return unwrap(res);
}

export async function setBalance(
  id: string,
  body: { balance: number; balanceExpiryDate: string },
): Promise<void> {
  // Backend accepts YYYY-MM-DD or ISO-8601 for expiryDate
  const res = await api.post<ResponseEnvelope<void>>(Endpoints.dashboard.setBalance(id), {
    balance: body.balance,
    expiryDate: body.balanceExpiryDate,
  });
  unwrap(res);
}

export async function addBalance(
  id: string,
  body: { amount: number },
): Promise<void> {
  const res = await api.post<ResponseEnvelope<void>>(Endpoints.dashboard.addBalance(id), {
    balance: body.amount,
  });
  unwrap(res);
}

export async function changeBalanceExpiry(
  id: string,
  body: { balanceExpiryDate: string },
): Promise<void> {
  // Backend accepts YYYY-MM-DD or ISO-8601 for expiryDate
  const res = await api.post<ResponseEnvelope<void>>(Endpoints.dashboard.changeExpiry(id), {
    expiryDate: body.balanceExpiryDate,
  });
  unwrap(res);
}

// Returns { apiKey } — matches AdminForceRotateKeyService
export async function rotateClientKey(id: string): Promise<RegisterResult> {
  const res = await api.post<ResponseEnvelope<RegisterResult>>(
    Endpoints.dashboard.rotateKey(id),
  );
  return unwrap(res);
}

export interface ServicePricing {
  id: string | null;
  clientId: string;
  serviceType: string;
  deductionAmount: number;
  active: boolean;
}

export async function fetchClientPricing(id: string): Promise<ServicePricing[]> {
  const res = await api.get<ResponseEnvelope<ServicePricing[]>>(Endpoints.dashboard.pricing(id));
  return unwrap(res);
}

export async function setClientPricing(
  id: string,
  body: { serviceType: string; deductionAmount: number; active: boolean },
): Promise<ServicePricing> {
  const res = await api.put<ResponseEnvelope<ServicePricing>>(Endpoints.dashboard.pricing(id), body);
  return unwrap(res);
}

export async function setBalanceTransfer(id: string, enabled: boolean): Promise<ClientDto> {
  const res = await api.put<ResponseEnvelope<ClientDto>>(
    `${Endpoints.dashboard.balanceTransfer(id)}?enabled=${enabled}`,
  );
  return unwrap(res);
}

// Matches org.authmatech.data.dto.OnboardingResultDto
// Dashboard credentials shown ONCE. API key is generated separately after first login.
export interface OnboardingResult {
  clientId: string;
  companyName: string;
  packageName: string | null;
  enabledProducts: string[] | null;
  dashboardUrl: string | null;
  username: string;
  temporaryPassword: string | null;
  keycloakUserId: string | null;
  keycloakRole: string;
  keycloakNote: string | null;
}

export interface FullOnboardBody {
  companyName: string;
  firstName: string;
  lastName: string;
  contactPersonEmail: string;
  contactPhoneNumber?: string;
  expectedAuthVolume?: string;
  packageName?: string;
  startingBalance: number;
  expiryDate: string;
  userRole: string;
  enabledProducts?: string[];
}

export async function fullOnboardClient(body: FullOnboardBody): Promise<OnboardingResult> {
  const res = await api.post<ResponseEnvelope<OnboardingResult>>(
    Endpoints.dashboard.fullOnboard,
    body,
  );
  return unwrap(res);
}

// Matches org.authmatech.data.dto.GenerateClientApiKeyResultDto
export interface GenerateApiKeyResult {
  clientId: string;
  rawApiKey: string;
  clientSecret: string;
  rawSdkToken: string;
}

export async function generateMyApiKey(): Promise<GenerateApiKeyResult> {
  const res = await api.post<ResponseEnvelope<GenerateApiKeyResult>>(
    Endpoints.me.apiKeyGenerate,
  );
  return unwrap(res);
}

export async function rotateMyApiKey(clientSecret: string): Promise<{ newApiKey: string }> {
  const res = await api.post<ResponseEnvelope<{ newApiKey: string }>>(
    Endpoints.me.apiKeyRotate,
    { secretKey: clientSecret },
  );
  return unwrap(res);
}

export async function rotateMySdkToken(clientSecret: string): Promise<{ sdkToken: string }> {
  const res = await api.post<ResponseEnvelope<{ sdkToken: string }>>(
    Endpoints.me.sdkTokenRotate,
    { secretKey: clientSecret },
  );
  return unwrap(res);
}

export async function fetchMyClientSecret(): Promise<{ clientSecret: string }> {
  const res = await api.get<ResponseEnvelope<{ clientSecret: string }>>(Endpoints.me.clientSecret);
  return unwrap(res);
}
